import React from "react";
import { type Room } from "matrix-js-sdk/src/matrix";

import AutocompleteProvider from "./AutocompleteProvider";
import { _t } from "../languageHandler";
import { MatrixClientPeg } from "../MatrixClientPeg";
import { PillCompletion } from "./Components";
import { type ICompletion, type ISelectionRange } from "./Autocompleter";
import RoomAvatar from "../components/views/avatars/RoomAvatar";
import { type TimelineRenderingType } from "../contexts/RoomContext";

const AT_ROOM_REGEX = /@\S*/g;

export default class NotifProvider extends AutocompleteProvider {
    public constructor(
        public room: Room,
        renderingType?: TimelineRenderingType,
    ) {
        super({ commandRegex: AT_ROOM_REGEX, renderingType });
    }

    public async getCompletions(
        query: string,
        selection: ISelectionRange,
        force = false,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        limit = -1,
    ): Promise<ICompletion[]> {
        const client = MatrixClientPeg.safeGet();

        // Room tags to trigger the @room completion, translated in all languages, removing spaces and punctuation for easier matching
        const roomTags = ["@chat", "@room", "@channel", "@everyone", "@here"];
        // Tag chat tradotto in tutte le lingue, rimuovendo spazi e punteggiatura per facilitare il matching
        const chatLocalized = _t("composer.autocomplete.@room_tag_name").replace(/[^a-z]/gi, "").toLowerCase();
        if (chatLocalized) {
            const localizedTag = `@${chatLocalized}`;
            if (!roomTags.includes(localizedTag)) roomTags.push(localizedTag);
        }

        if (!this.room.currentState.mayTriggerNotifOfType("room", client.getSafeUserId())) return [];

        const { command, range } = this.getCurrentCommand(query, selection, force);
        if (
            command?.[0] &&
            command[0].length > 1 &&
            roomTags.some((c) => c.startsWith(command![0]))
        ) {
            return [
                {
                    completion: "@room",
                    completionId: "@room",
                    type: "at-room",
                    suffix: " ",
                    component: (
                        <PillCompletion title="@room" description={_t("composer.autocomplete.@room_description")}>
                            <RoomAvatar size="24px" room={this.room} />
                        </PillCompletion>
                    ),
                    range: range!,
                },
            ];
        }
        return [];
    }

    public getName(): string {
        return "❗️ " + _t("composer.autocomplete.notification_description");
    }

    public renderCompletions(completions: React.ReactNode[]): React.ReactNode {
        return (
            <div
                className="mx_Autocomplete_Completion_container_pill mx_Autocomplete_Completion_container_truncate"
                role="presentation"
                aria-label={_t("composer.autocomplete.notification_a11y")}
            >
                {completions}
            </div>
        );
    }
}
